import { React, useState, useEffect, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import AdxInput from 'components/custom/AdxInput'
import { InputAdornment } from '@mui/material'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import { Formik, Form, ErrorMessage } from 'formik'
import * as yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
import { postBankData } from 'services/api/modules/staff-management/staffManagementDashboard'
import { recalculateSelectedEmployee } from 'services/api/modules/payroll/payroll-view'

import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import Bugsnag from '@bugsnag/js'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = yup.object({
  epf_status: yup.number().nullable().required('EPF Contribution Status is required'),
  epf_category: yup
    .object()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: yup.object().nullable().required('EPF Category is required'),
      otherwise: yup.object().nullable(),
    }),
  epf_acc_number: yup
    .string()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: yup
        .string()
        .nullable()
        // .required("EPF Account Number is required")
        .matches(/^[a-z\d]+$/i, 'EPF Account Number can contain only alphanumeric'),
      otherwise: yup.string().nullable(),
    }),
  epf_employee_contribution_rate: yup
    .number()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: yup.number().nullable().required('Employee EPF Rate is required'),
      otherwise: yup.number().nullable(),
    }),
  employee_custom_contribution_rate: yup
    .string()
    .nullable()
    .when('epf_employee_contribution_rate', {
      is: 2,
      then: yup
        .string()
        .nullable()
        .required('Custom Rate is required')
        .matches(/^(0?[0-9]|1[0-9])$/, 'Custom Rate must not be more than 19%'),
      otherwise: yup.string().nullable(),
    }),
  epf_employer_contribution_rate: yup
    .number()
    .nullable()
    .when('epf_status', {
      is: 1,
      then: yup.number().nullable().required('Employer EPF Rate is required'),
      otherwise: yup.number().nullable(),
    }),
  employer_custom_contribution_rate: yup
    .string()
    .nullable()
    .when('epf_employer_contribution_rate', {
      is: 1,
      then: yup
        .string()
        .nullable()
        .required('Custom Rate is required')
        .matches(/^(0?[0-9]|1[0-9])$/, 'Custom Rate must not be more than 19%'),
      otherwise: yup.string().nullable(),
    }),
  socso_worker_type: yup.number().nullable().required('SOCSO Worker Type is required'),
  socso_status: yup.number().nullable().required('SOCSO Contribution Status is required'),
  socso_category: yup
    .object()
    .nullable()
    .when('socso_status', {
      is: 1,
      then: yup.object().nullable().required('SOCSO Category is required'),
      otherwise: yup.object().nullable(),
    }),
  socso_acc_number: yup
    .string()
    .nullable()
    .when('socso_status', {
      is: 1,
      then: yup
        .string()
        .nullable()
        // .required("SOCSO Account Number is required")
        .matches(/^[a-z\d]+$/i, 'SOCSO Account Number can contain only alphanumeric'),
      otherwise: yup.string().nullable(),
    }),
  skbbk_deduction: yup.number().nullable().required('SKBBK Contribution Status is required'),
  eis_status: yup.number().nullable().required('EIS Contribution Status is required'),
  zakat_status: yup.number().nullable().required('Zakat Contribution Status is required'),
  zakat_acc_number: yup
    .string()
    .nullable()
    .when('zakat_status', {
      is: 1,
      then: yup
        .string()
        .nullable()
        // .required("Zakat Account Number is required")
        .matches(/^[a-z\d]+$/i, 'Zakat Account Number can contain only alphanumeric'),
      otherwise: yup.string().nullable(),
    }),
  zakat_amount: yup
    .string()
    .nullable()
    .when('zakat_status', {
      is: 1,
      then: yup
        .string()
        .nullable()
        .required('Zakat Amount is required')
        .matches(/^[0-9]{1,11}(?:\.[0-9]{1,3})?$/, 'Zakat Amount must be valid'),
      otherwise: yup.string().nullable(),
    }),
})

const EPFCategory = [
  {
    id: 1,
    category: 'Category 1',
    description:
      'Malaysian/Permanent Residents/Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 2,
    category: 'Category 2',
    description:
      'Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 3,
    category: 'Category 3',
    description:
      'Permanent Residents/Non-Malaysians (registered as member before 01/08/1998) AND age below 60 years old',
  },
  {
    id: 4,
    category: 'Category 4',
    description:
      'Non-Malaysians (registered as member before 01/08/1998) AND age 60 years old and above',
  },
  {
    id: 5,
    category: 'Category 5',
    description: 'Malaysians AND age 60 years old and above',
  },
]

const EPFEmployeeOneTwo = [
  { id: 0, epf_rate: '9%' },
  { id: 1, epf_rate: '11%' },
  { id: 2, epf_rate: 'Custom' },
]

const EPFEmployeeThreeFour = [
  { id: 0, epf_rate: '5.5%' },
  { id: 2, epf_rate: 'Custom' },
]

const EPFEmployeeFive = [
  { id: 0, epf_rate: '0%' },
  { id: 2, epf_rate: 'Custom' },
]

const EPFEmployerRate = [
  { id: 0, epf_rate: 'Fixed' },
  { id: 1, epf_rate: 'Custom' },
]

const SOCSOCategoryLocal = [
  {
    id: 1,
    category: 'Category 1',
    description:
      'For employees who are less than 60 years of age, except for those who have attained 55 years of age and have no prior contributions before they reach 55',
  },
  {
    id: 2,
    category: 'Category 2',
    description:
      'For employees who have reached the age of 60 OR for eligible new employees who are 55 years of age',
  },
]

const SOCSOCategoryForeign = [
  {
    id: 1,
    category: 'Category 1 (Injury + Invalidity)',
    description:
      'For foreign workers who first enter or contribute for the first time under PERKESO Invalidity Scheme under the age of 55',
  },
  {
    id: 2,
    category: 'Category 2 (Injury only)',
    description:
      'For foreign workers who have reached 55 years old or older at first entry to the scheme OR who have reached 60 years old and are still working',
  },
]


const SOCSOWorkerTypeOptions = [
  { id: 1, label: 'Local employee' },
  { id: 2, label: 'Foreign worker' },
]

const StatutoryBodyDetails = ({ data, employeeId, time_id, isMalaysian }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const [isLoading, setIsLoading] = useState(false)
  const [disable, setDisable] = useState(true)

  const [initialValues, setInitialValues] = useState({
    epf_status: '',
    epf_category: '',
    epf_acc_number: '',
    epf_employee_contribution_rate: '',
    employee_custom_contribution_rate: '',
    epf_employer_contribution_rate: '',
    employer_custom_contribution_rate: '',
    socso_worker_type: 1,
    socso_status: '',
    socso_category: '',
    socso_acc_number: '',
    skbbk_deduction: '',
    eis_status: '',
    zakat_status: '',
    zakat_acc_number: '',
    zakat_amount: '',
  })

  // RECALCULATE
  const { mutate: recalculatePayroll } = useMutation(recalculateSelectedEmployee, {
    onSuccess: () => {
      setIsLoading(false)
      setDisable(true)
      queryClient.invalidateQueries('retrieveEmployeeData')
      queryClient.invalidateQueries('employeeBankData')
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const mutation = useMutation((value) => postBankData(employeeId, value), {
    onSuccess: () => {
      setIsLoading(true)
      recalculatePayroll({ time_id, employee_id: employeeId, data: {} })
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitStatutoryBody = (values) => {
    const newValues = {
      ...values,
      epf_category: values.epf_category ? values.epf_category.id : null,
      socso_category: values.socso_category ? values.socso_category.id : null,
      skbbk_deduction: values.socso_status === 1 ? values.skbbk_deduction : 0,
    }
    mutation.mutate(newValues)
  }

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  useEffect(() => {
    if (data) {
      const derivedWorkerType =
        data?.data?.socso_worker_type ??
        (typeof isMalaysian === 'boolean' ? (isMalaysian ? 1 : 2) : 1)

      // Foreign worker → force EIS = 0
      const resolvedEisStatus =
        derivedWorkerType === 2 ? 0 : data?.data?.eis_status

      // Pick the correct category list based on worker type
      const categoryList =
        derivedWorkerType === 2 ? SOCSOCategoryForeign : SOCSOCategoryLocal

      let initialSocsoCategory = null
      if (data?.data?.socso_category) {
        initialSocsoCategory =
          categoryList.find((item) => item.id === data.data.socso_category) || null
      }

      const effectiveSocsoCategory =
        data?.data?.socso_status === 1 ? initialSocsoCategory : null

      setInitialValues({
        epf_status: data?.data?.epf_status,
        epf_category: data?.data?.epf_category
          ? EPFCategory.find((item) => item.id === data?.data?.epf_category)
          : { id: 0, category: 'No Category' },
        epf_acc_number: data?.data?.epf_acc_number || '',
        epf_employee_contribution_rate: data?.data?.epf_employee_contribution_rate,
        employee_custom_contribution_rate:
          data?.data?.employee_custom_contribution_rate || 0,
        epf_employer_contribution_rate: data?.data?.epf_employer_contribution_rate,
        employer_custom_contribution_rate:
          data?.data?.employer_custom_contribution_rate || 0,

        socso_worker_type: derivedWorkerType,
        socso_status: data?.data?.socso_status,
        socso_category: effectiveSocsoCategory,
        socso_acc_number: data?.data?.socso_acc_number || '',
        skbbk_deduction: data?.data?.socso_status === 1 ? data?.data?.skbbk_deduction ?? 0 : 0,
        eis_status: resolvedEisStatus,

        zakat_status: data?.data?.zakat_status,
        zakat_acc_number: data?.data?.zakat_acc_number,
        zakat_amount: data?.data?.zakat_amount || 0,
      })
    }
  }, [data, isMalaysian])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitStatutoryBody}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleSubmit,
        setFieldValue,
        resetForm,
      }) => {
        // 👇 choose list based on worker type
        const categoryOptions =
          values.socso_worker_type === 2
            ? SOCSOCategoryForeign
            : SOCSOCategoryLocal


        return (
          <Form>
            <Box
              p={1.75}
              display="flex"
              justifyContent="space-between"
              borderBottom="1px solid"
              sx={{ borderColor: 'gray-line.main' }}
              alignItems="center"
            >
              <Typography color="light-gray-text.main" variant="subtitle2" fontWeight={500}>
                Statutory body details
              </Typography>
              {disable && (
                <Typography
                  sx={{
                    textDecoration: 'underline',
                    color: 'alternate.main',
                    cursor: 'pointer',
                  }}
                  onClick={() => setDisable(false)}
                >
                  Edit
                </Typography>
              )}
            </Box>

            <Box p={1.75} sx={{ borderColor: 'gray-line.main' }}>
              {/* EPF status */}
              <Box>
                <AdxDropdown
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  label="EPF contribution status"
                  disabled={disable}
                  size="standard"
                  value={values.epf_status}
                  name="epf_status"
                  onChange={(event) => {
                    if (values.epf_status === 1) {
                      if (event.target.value !== 1) {
                        setFieldValue('epf_category', { id: 0, category: 'No Category' })
                        setFieldValue('epf_acc_number', null)
                        setFieldValue('epf_employee_contribution_rate', 0)
                        setFieldValue('employee_custom_contribution_rate', 0)
                        setFieldValue('epf_employer_contribution_rate', 0)
                        setFieldValue('employer_custom_contribution_rate', 0)
                      }
                    } else {
                      if (event.target.value === 1) {
                        setFieldValue('epf_category', null)
                        setFieldValue('epf_acc_number', null)
                        setFieldValue('epf_employee_contribution_rate', null)
                        setFieldValue('employee_custom_contribution_rate', 0)
                        setFieldValue('epf_employer_contribution_rate', null)
                        setFieldValue('employer_custom_contribution_rate', 0)
                      } else {
                        setFieldValue('epf_category', { id: 0, category: 'No Category' })
                        setFieldValue('epf_acc_number', null)
                        setFieldValue('epf_employee_contribution_rate', 0)
                        setFieldValue('employee_custom_contribution_rate', 0)
                        setFieldValue('epf_employer_contribution_rate', 0)
                        setFieldValue('employer_custom_contribution_rate', 0)
                      }
                    }
                    handleChange(event)
                  }}
                  sx={{ width: { xs: '100%', sm: '40%' }, mb: 2.5, mt: 2 }}
                  errorMessage={<ErrorMessage name="epf_status" component={AdxErrorMessage} />}
                  isErrorShown={touched.epf_status && errors.epf_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.epf_status}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.epf_status}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>

              {/* EPF category */}
              <Box gap={2.5} display="flex" alignItems="center" mb={2.5} mt={2}>
                <AdxDropdown
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  label="EPF contribution category"
                  disabled={disable ? true : values.epf_status === 1 ? false : true}
                  size="standard"
                  placeholder="EPF contribution category"
                  value={values.epf_category}
                  name="epf_category"
                  sx={{ width: '100%' }}
                  onChange={(tre) => {
                    if (values?.epf_category?.id === 1 || values?.epf_category?.id === 2) {
                      if (tre.target.value.id === 1 || tre.target.value.id === 2) {
                        handleChange(tre)
                      } else {
                        handleChange(tre)
                        setFieldValue('epf_employee_contribution_rate', null)
                      }
                    } else {
                      if (tre.target.value.id === 1 || tre.target.value.id === 2) {
                        handleChange(tre)
                        setFieldValue('epf_employee_contribution_rate', null)
                      } else {
                        handleChange(tre)
                      }
                    }
                  }}
                  renderValue={(value) => (
                    <Box>
                      <Typography>{value.category}</Typography>
                    </Box>
                  )}
                  errorMessage={<ErrorMessage name="epf_category" component={AdxErrorMessage} />}
                  isErrorShown={touched.epf_category && errors.epf_category}
                >
                  {EPFCategory.map((item) => (
                    <AdxMenuItem key={item.id} value={item} selectedvalue={values.epf_category}>
                      <Box p={2}>
                        <Typography color="dark-text.main" pb={0.375}>
                          {item.category}
                        </Typography>
                        <Typography color="light-gray-text.main">
                          {item.description}
                        </Typography>
                      </Box>
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>

              {/* EPF account */}
              <Box>
                <AdxInput
                  label="EPF account no."
                  disabled={disable ? true : values.epf_status === 1 ? false : true}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="EPF account no."
                  value={values.epf_acc_number}
                  name="epf_acc_number"
                  onChange={handleChange}
                  sx={{ width: '100%', mb: 2.5 }}
                  errorMessage={
                    <ErrorMessage name="epf_acc_number" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.epf_acc_number && errors.epf_acc_number}
                />
              </Box>

              {/* Employee EPF rate */}
              <Box
                gap={2.5}
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                mb={2.5}
                mt={2}
              >
                <Box width={{ xs: '100%', sm: '40%' }}>
                  <AdxDropdown
                    label="Employee EPF Rate"
                    disabled={disable ? true : values.epf_status === 1 ? false : true}
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="Employee EPF Rate"
                    value={values.epf_employee_contribution_rate}
                    name="epf_employee_contribution_rate"
                    onChange={(event) => {
                      setFieldValue('employee_custom_contribution_rate', 0)
                      handleChange(event)
                    }}
                    errorMessage={
                      <ErrorMessage
                        name="epf_employee_contribution_rate"
                        component={AdxErrorMessage}
                      />
                    }
                    isErrorShown={
                      touched.epf_employee_contribution_rate &&
                      errors.epf_employee_contribution_rate
                    }
                  >
                    {values?.epf_category?.id === 1 || values?.epf_category?.id === 2
                      ? EPFEmployeeOneTwo.map((rate) => (
                        <AdxMenuItem
                          key={rate.id}
                          value={rate.id}
                          selectedvalue={values.epf_employee_contribution_rate}
                        >
                          {rate.epf_rate}
                        </AdxMenuItem>
                      ))
                      : values?.epf_category?.id === 3 || values?.epf_category?.id === 4
                        ? EPFEmployeeThreeFour.map((rate) => (
                          <AdxMenuItem
                            key={rate.id}
                            value={rate.id}
                            selectedvalue={values.epf_employee_contribution_rate}
                          >
                            {rate.epf_rate}
                          </AdxMenuItem>
                        ))
                        : EPFEmployeeFive.map((rate) => (
                          <AdxMenuItem
                            key={rate.id}
                            value={rate.id}
                            selectedvalue={values.epf_employee_contribution_rate}
                          >
                            {rate.epf_rate}
                          </AdxMenuItem>
                        ))}
                  </AdxDropdown>
                </Box>
                <Box width={{ xs: '100%', sm: '25%' }}>
                  {values.epf_employee_contribution_rate === 2 && (
                    <AdxInput
                      label="Custom rate"
                      disabled={disable ? true : values.epf_status === 1 ? false : true}
                      sxLabel={{
                        color: disable ? 'light-gray-text.main' : 'dark-text.main',
                      }}
                      sxInput={{
                        color: 'light-gray-text.main',
                        backgroundColor: 'white.main',
                      }}
                      size="standard"
                      placeholder="Custom rate"
                      endAdornment={<InputAdornment position="end">%</InputAdornment>}
                      value={values.employee_custom_contribution_rate}
                      name="employee_custom_contribution_rate"
                      onChange={handleChange}
                      errorMessage={
                        <ErrorMessage
                          name="employee_custom_contribution_rate"
                          component={AdxErrorMessage}
                        />
                      }
                      isErrorShown={
                        touched.employee_custom_contribution_rate &&
                        errors.employee_custom_contribution_rate
                      }
                    />
                  )}
                </Box>
              </Box>

              {/* Employer EPF rate */}
              <Box
                gap={2.5}
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                mb={2.5}
                mt={2}
              >
                <Box width={{ xs: '100%', sm: '40%' }}>
                  <AdxDropdown
                    label="Employer EPF Rate"
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    disabled={disable ? true : values.epf_status === 1 ? false : true}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="Employer EPF Rate"
                    name="epf_employer_contribution_rate"
                    value={values.epf_employer_contribution_rate}
                    onChange={(event) => {
                      setFieldValue('employer_custom_contribution_rate', 0)
                      handleChange(event)
                    }}
                    errorMessage={
                      <ErrorMessage
                        name="epf_employer_contribution_rate"
                        component={AdxErrorMessage}
                      />
                    }
                    isErrorShown={
                      touched.epf_employer_contribution_rate &&
                      errors.epf_employer_contribution_rate
                    }
                    sx={{ mb: 6.25 }}
                  >
                    {EPFEmployerRate.map((rate) => (
                      <AdxMenuItem
                        key={rate.id}
                        value={rate.id}
                        selectedvalue={values.epf_employer_contribution_rate}
                      >
                        {rate.epf_rate}
                      </AdxMenuItem>
                    ))}
                  </AdxDropdown>
                </Box>
                <Box width={{ xs: '100%', sm: '25%' }}>
                  {values.epf_employer_contribution_rate === 1 && (
                    <AdxInput
                      label="Custom rate"
                      disabled={disable ? true : values.epf_status === 1 ? false : true}
                      sxLabel={{
                        color: disable ? 'light-gray-text.main' : 'dark-text.main',
                      }}
                      sxInput={{
                        color: 'light-gray-text.main',
                        backgroundColor: 'white.main',
                      }}
                      size="standard"
                      placeholder="Custom rate"
                      endAdornment={<InputAdornment position="end">%</InputAdornment>}
                      value={values.employer_custom_contribution_rate}
                      name="employer_custom_contribution_rate"
                      onChange={handleChange}
                      errorMessage={
                        <ErrorMessage
                          name="employer_custom_contribution_rate"
                          component={AdxErrorMessage}
                        />
                      }
                      isErrorShown={
                        touched.employer_custom_contribution_rate &&
                        errors.employer_custom_contribution_rate
                      }
                    />
                  )}
                </Box>
              </Box>

              {/* SOCSO contribution status */}
              <Box width={{ xs: '100%', sm: '40%' }}>


                <AdxDropdown
                  label="SOCSO contribution status"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="SOCSO contribution status"
                  value={values.socso_status}
                  name="socso_status"

                  onChange={(event) => {
                    const nextStatus = Number(event.target.value)

                    if (values.socso_status === 1 && nextStatus !== 1) {
                      setFieldValue('socso_category', null)
                      setFieldValue('socso_acc_number', null)
                      setFieldValue('skbbk_deduction', 0)
                    }
                    if (nextStatus === 1) {
                      const workerType = values.socso_worker_type ?? 1
                      const list =
                        workerType === 2 ? SOCSOCategoryForeign : SOCSOCategoryLocal

                      if (!values.socso_category) {
                        setFieldValue('socso_category', list[0])
                      }
                      setFieldValue('skbbk_deduction', data?.data?.skbbk_deduction ?? 1)
                    }
                    handleChange(event)
                  }}

                  sx={{ mb: 2.5 }}
                  errorMessage={
                    <ErrorMessage name="socso_status" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.socso_status && errors.socso_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.socso_status}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.socso_status}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>

              {/* SOCSO worker type */}
              <Box width={{ xs: '100%', sm: '40%' }}>

                <AdxDropdown
                  label="SOCSO worker type"
                  disabled={disable || isMalaysian || values.socso_status === 0}
                  sxLabel={{
                    color:
                      disable || isMalaysian ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="SOCSO worker type"
                  value={values.socso_worker_type ?? 1}
                  name="socso_worker_type"
                  onChange={(event) => {
                    const prevType = values.socso_worker_type ?? 1
                    const newType = Number(event.target.value)

                    handleChange(event)

                    // Foreign worker → EIS must be 0, categories = foreign text
                    if (newType === 2) {
                      setFieldValue('eis_status', 0)

                      if (values.socso_status === 1) {
                        const list = SOCSOCategoryForeign
                        const nextCategory =
                          values.socso_category &&
                            list.some((c) => c.id === values.socso_category.id)
                            ? list.find((c) => c.id === values.socso_category.id)
                            : list[0]

                        setFieldValue('socso_category', nextCategory)
                      } else {
                        setFieldValue('socso_category', null)
                      }
                    }

                    // Back to local employee
                    if (prevType === 2 && newType === 1) {
                      if (values.socso_status === 1) {
                        const list = SOCSOCategoryLocal
                        const nextCategory =
                          values.socso_category &&
                            list.some((c) => c.id === values.socso_category.id)
                            ? list.find((c) => c.id === values.socso_category.id)
                            : list[0]

                        setFieldValue('socso_category', nextCategory)
                      } else {
                        setFieldValue('socso_category', null)
                      }

                      // If previously forced to 0, allow HR to turn EIS back on
                      if (values.eis_status === 0) {
                        setFieldValue('eis_status', 1)
                      }
                    }
                  }}
                  sx={{ mb: 2.5 }}
                  errorMessage={
                    <ErrorMessage name="socso_worker_type" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.socso_worker_type && errors.socso_worker_type}
                >
                  {SOCSOWorkerTypeOptions.map((opt) => (
                    <AdxMenuItem
                      key={opt.id}
                      value={opt.id}
                      selectedvalue={values.socso_worker_type ?? 1}
                    >
                      {opt.label}
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>

              {/* SOCSO category */}
              <Box gap={2.5} display="flex" alignItems="center" mb={2.5}>
                <Box width="100%">
                  <AdxDropdown
                    label="SOCSO contribution category"
                    disabled={disable || values.socso_status !== 1}
                    sxLabel={{
                      color: disable ? 'light-gray-text.main' : 'dark-text.main',
                    }}
                    sxInput={{
                      color: 'light-gray-text.main',
                      backgroundColor: 'white.main',
                    }}
                    size="standard"
                    placeholder="SOCSO contribution category"
                    value={values.socso_category}
                    name="socso_category"
                    onChange={handleChange}
                    renderValue={(value) => (
                      <Box>
                        <Typography>{value.category}</Typography>
                      </Box>
                    )}
                    errorMessage={
                      <ErrorMessage name="socso_category" component={AdxErrorMessage} />
                    }
                    isErrorShown={touched.socso_category && errors.socso_category}
                  >
                    {categoryOptions.map((item) => (
                      <AdxMenuItem
                        key={item.id}
                        value={item}
                        selectedvalue={values.socso_category}
                      >
                        <Box p={2}>
                          <Typography color="dark-text.main" pb={0.375}>
                            {item.category}
                          </Typography>
                          <Typography color="light-gray-text.main">
                            {item.description}
                          </Typography>
                        </Box>
                      </AdxMenuItem>
                    ))}
                  </AdxDropdown>
                </Box>
              </Box>



              {/* SOCSO account */}
              <Box width="100%">
                <AdxInput
                  label="SOCSO account no."
                  disabled={disable ? true : values.socso_status === 1 ? false : true}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="SOCSO account no."
                  value={values.socso_acc_number}
                  name="socso_acc_number"
                  onChange={handleChange}
                  sx={{ mb: 6.25 }}
                  errorMessage={
                    <ErrorMessage name="socso_acc_number" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.socso_acc_number && errors.socso_acc_number}
                />
              </Box>

              {/* SKBBK status */}
              <Box width={{ xs: '100%', sm: '40%' }}>
                <AdxDropdown
                  label="SKBBK contribution status"
                  disabled={disable || values.socso_status !== 1}
                  sxLabel={{
                    color:
                      disable || values.socso_status !== 1
                        ? 'light-gray-text.main'
                        : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="SKBBK contribution status"
                  value={values.skbbk_deduction}
                  name="skbbk_deduction"
                  onChange={handleChange}
                  sx={{ mb: 2.5 }}
                  errorMessage={
                    <ErrorMessage name="skbbk_deduction" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.skbbk_deduction && errors.skbbk_deduction}
                >
                  <AdxMenuItem value={1} selectedvalue={values.skbbk_deduction}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.skbbk_deduction}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>

              {/* EIS status + note */}
              <Box
                width={{ xs: '100%', sm: '40%' }}
                sx={{
                  mb: values.socso_worker_type === 2 ? 2 : 6.5,
                }}
              >
                <AdxDropdown
                  label="EIS contribution status"
                  disabled={disable || values.socso_worker_type === 2}
                  sxLabel={{
                    color:
                      disable || values.socso_worker_type === 2
                        ? 'light-gray-text.main'
                        : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="EIS contribution status"
                  value={values.eis_status}
                  name="eis_status"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="eis_status" component={AdxErrorMessage} />}
                  isErrorShown={touched.eis_status && errors.eis_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.eis_status}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.eis_status}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>

                {values.socso_worker_type === 2 && !disable && (
                  <Typography variant="caption" color="light-gray-text.main" sx={{ mt: 0.5 }}>
                    Foreign workers are not covered by EIS. EIS is for Malaysian citizens only.
                  </Typography>
                )}
              </Box>

              {/* Zakat status */}
              <Box width={{ xs: '100%', sm: '40%' }}>
                <AdxDropdown
                  label="Zakat contribution status"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Zakat contribution status"
                  value={values.zakat_status}
                  name="zakat_status"
                  onChange={(event) => {
                    if (values.zakat_status === 1 && event.target.value !== 1) {
                      setFieldValue('zakat_acc_number', null)
                    }
                    setFieldValue('zakat_amount', 0)
                    handleChange(event)
                  }}
                  sx={{ mb: 2.5 }}
                  errorMessage={
                    <ErrorMessage name="zakat_status" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.zakat_status && errors.zakat_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.zakat_status}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.zakat_status}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>

              {/* Zakat details */}
              {values.zakat_status === 1 && (
                <>
                  <Box width="100%">
                    <AdxInput
                      label="Zakat Account Number"
                      disabled={disable}
                      sxLabel={{
                        color: disable ? 'light-gray-text.main' : 'dark-text.main',
                      }}
                      sxInput={{
                        color: 'light-gray-text.main',
                        backgroundColor: 'white.main',
                      }}
                      size="standard"
                      placeholder="Zakat Account Number"
                      value={values.zakat_acc_number}
                      name="zakat_acc_number"
                      onChange={handleChange}
                      sx={{ mb: 2.5 }}
                      errorMessage={
                        <ErrorMessage
                          name="zakat_acc_number"
                          component={AdxErrorMessage}
                        />
                      }
                      isErrorShown={touched.zakat_acc_number && errors.zakat_acc_number}
                    />
                  </Box>

                  <Box
                    width={{ xs: '100%', sm: '60%' }}
                    sx={{ display: 'flex', alignItems: 'flex-start' }}
                  >
                    <AdxInput
                      label="Zakat amount"
                      disabled={disable}
                      sxLabel={{
                        color: disable ? 'light-gray-text.main' : 'dark-text.main',
                      }}
                      sxInput={{
                        color: 'light-gray-text.main',
                        backgroundColor: 'white.main',
                      }}
                      size="standard"
                      placeholder="XX,XXX"
                      value={values.zakat_amount}
                      name="zakat_amount"
                      onChange={handleChange}
                      startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                      sx={{ width: { xs: '70%', sm: '65%' }, mr: 2 }}
                      errorMessage={
                        <ErrorMessage name="zakat_amount" component={AdxErrorMessage} />
                      }
                      isErrorShown={touched.zakat_amount && errors.zakat_amount}
                    />
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      alignItems="flex-start"
                    >
                      <Typography sx={{ mt: 5 }} variant="body1" color="light-gray-text.main">
                        (per month)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}

              {/* Footer buttons */}
              {!disable && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: 1,
                    mt: 2,
                  }}
                >
                  <AdxButton
                    variant="outlined"
                    size="small"
                    onClick={() => cancelEditForm(resetForm)}
                    sx={{ width: 0.2 }}
                  >
                    Cancel
                  </AdxButton>
                  {isLoading ? (
                    <AdxButton variant="contained" size="small" sx={{ width: 0.2 }}>
                      <CircularProgress color="white" size={20} />
                    </AdxButton>
                  ) : (
                    <AdxButton
                      variant="contained"
                      size="small"
                      onClick={() => handleCheckRef(handleSubmit)}
                      sx={{ width: 0.2 }}
                    >
                      Save
                    </AdxButton>
                  )}
                </Box>
              )}
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}

export default StatutoryBodyDetails
